import { FunctionComponent, RefObject, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import moment, { Moment } from 'moment-timezone'
import TimePickerComponent from '@/components/_custom/Picker/timePicker/timePicker'
import { useQuery } from '@apollo/client'
import { GET_STAFF_PREPARATION_TASKS_BY_DAYCARE } from '@/modules/API/staff/staffPreparationTask'
import { StaffPreparationTask } from '@/types/StaffPreparationTask'
import { SelectOptionsDeprecated } from '@/types/Select'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple'
import { WorkType } from '@/types/work'
import styles from './dayTimeRow.module.scss'
import ButtonIcon from '@/components/_custom/Buttons/ButtonIcon/ButtonIcon'
import { IconButton, Tooltip } from '@mui/material'
import { Add, Close, Visibility, VisibilityOff } from '@mui/icons-material'
import { useDialog } from '@/hooks/useDialog/useDialog'
import StaffPreparationTaskComponent from '@/container/daycare/daycareProfile/staff/modals/staffPreparationTask'
import { Daycare } from '@/types/Daycare'

interface DayTimeRowProps {
    // Set for one row per selected day (recurrence mode): shows the day name on the left and
    // widens the grid to make room for it. Left unset for the single day/date-range mode, which
    // renders a lone, label-less row.
    dayLabel?: string
    onPreview: () => void
    previewActive: boolean
    start: string
    end: string
    setStart: (value: string) => void
    setEnd: (value: string) => void
    minHour: number
    minMinute: number
    maxHour: number
    maxMinute: number
    minuteToDisable: number
    workType: string
    hasPause: boolean
    setHasPause: (value: boolean) => void
    pauseStart: string
    setPauseStart: (value: string) => void
    pauseEnd: string
    setPauseEnd: (value: string) => void
    showPreparationFields: boolean
    daycare: Daycare
    preparationBefore: string[]
    setPreparationBefore: (value: string[]) => void
    preparationAfter: string[]
    setPreparationAfter: (value: string[]) => void
    canUpdatePreparationTasks: boolean
}

// One start/end/pause/preparation-tasks row, shared by both the single day and the per-day
// recurrence layouts (see editOrCreateWorkDialog.tsx) - `dayLabel` is the only thing that
// tells the two apart.
const DayTimeRow: FunctionComponent<DayTimeRowProps> = ({
    dayLabel,
    onPreview,
    previewActive,
    start,
    end,
    setStart,
    setEnd,
    minHour,
    minMinute,
    maxHour,
    maxMinute,
    minuteToDisable,
    workType,
    hasPause,
    setHasPause,
    pauseStart,
    setPauseStart,
    pauseEnd,
    setPauseEnd,
    showPreparationFields,
    daycare,
    preparationBefore,
    setPreparationBefore,
    preparationAfter,
    setPreparationAfter
}: DayTimeRowProps) => {
    const { t } = useTranslation()

    const {
        openDialogActionItem: actionItem,
        dialog: manageTasksDialog,
        reopenTrigger
    } = useDialog({
        dialogTitle: t('myStaffPreparationTasks'),
        Component: StaffPreparationTaskComponent,
        props: () => ({
            daycare
        }),
        resourceLabel: { type: 'manageResource', label: t('tasks').toLocaleLowerCase() }
    })

    const { data, refetch } = useQuery(GET_STAFF_PREPARATION_TASKS_BY_DAYCARE, {
        variables: { daycareId: daycare.id },
        skip: !showPreparationFields
    })

    const isFirstRender: RefObject<boolean> = useRef(true)

    useEffect((): void => {
        if (isFirstRender.current) {
            isFirstRender.current = false
            return
        }
        if (showPreparationFields) refetch()
    }, [reopenTrigger])

    useEffect((): void => {
        if (!showPreparationFields || !data?.getStaffPreparationTasksByDaycare) return

        const validIds: Set<string> = new Set(
            data.getStaffPreparationTasksByDaycare.map((task: StaffPreparationTask) => task.id.toString())
        )

        const filteredBefore: string[] = preparationBefore.filter((id: string): boolean => validIds.has(id))
        if (filteredBefore.length !== preparationBefore.length) setPreparationBefore(filteredBefore)

        const filteredAfter: string[] = preparationAfter.filter((id: string): boolean => validIds.has(id))
        if (filteredAfter.length !== preparationAfter.length) setPreparationAfter(filteredAfter)
    }, [data])

    const options: SelectOptionsDeprecated = [
        {
            options: (data?.getStaffPreparationTasksByDaycare || []).map((task: StaffPreparationTask) => ({
                value: task.id.toString(),
                name: `${task.name}  (${task.duration}min)`
            }))
        }
    ]

    const startMaxHour: number = end.length === 5 ? moment(end, 'HH:mm').hours() : maxHour
    const startMaxMinute: number = end.length === 5 ? moment(end, 'HH:mm').minutes() : maxMinute
    const endMinHour: number = start.length === 5 ? moment(start, 'HH:mm').hours() : minHour
    const endMinMinute: number = start.length === 5 ? moment(start, 'HH:mm').minutes() : minMinute

    const showPause: boolean = workType !== WorkType.holiday && start.length === 5 && end.length === 5

    function addPause(): void {
        const workStart: Moment = moment(start, 'HH:mm', true)
        const workEnd: Moment = moment(end, 'HH:mm', true)
        const duration: number = workEnd.diff(workStart, 'minutes')

        if (workStart.isValid() && workEnd.isValid() && duration >= 30) {
            let defaultStart: Moment = workStart
                .clone()
                .add(duration / 2, 'minutes')
                .startOf('hour')
            if (defaultStart.isBefore(workStart)) defaultStart = workStart.clone()
            if (defaultStart.clone().add(30, 'minutes').isAfter(workEnd)) {
                defaultStart = workEnd.clone().subtract(30, 'minutes')
            }
            setPauseStart(defaultStart.format('HH:mm'))
            setPauseEnd(defaultStart.clone().add(30, 'minutes').format('HH:mm'))
        }
        setHasPause(true)
    }

    function removePause(): void {
        setHasPause(false)
        setPauseStart('')
        setPauseEnd('')
    }

    return (
        <div className={`${styles.DayTimeRow} ${dayLabel ? styles.WithDayLabel : ''}`}>
            <Tooltip title={t('workSlotPreview')}>
                <IconButton
                    className={styles.PreviewButton}
                    onClick={onPreview}
                    aria-label={t('workSlotPreview')}
                    aria-pressed={previewActive}
                >
                    {previewActive ? <VisibilityOff /> : <Visibility />}
                </IconButton>
            </Tooltip>
            {dayLabel && <span className={styles.DayLabel}>{dayLabel}</span>}
            <span className={`${styles.ColumnLabel} ${styles.WorkLabel}`}>{t('work')}</span>
            <span className={`${styles.ColumnLabel} ${styles.PauseLabel}`}>
                {t('break')}
                {showPause && hasPause && (
                    <ButtonIcon icon={<Close />} className={styles.ButtonIcon} onClick={removePause} />
                )}
            </span>
            <TimePickerComponent
                value={start}
                setValue={setStart}
                minHour={minHour}
                minMinute={minMinute}
                maxHour={startMaxHour}
                maxMinute={startMaxMinute}
                label={t('startTime')}
                minuteToDisable={minuteToDisable}
                className={`${styles.DayTimeInput} ${styles.WorkStart}`}
            />
            <TimePickerComponent
                value={end}
                setValue={setEnd}
                minHour={endMinHour}
                minMinute={endMinMinute}
                maxHour={maxHour}
                maxMinute={maxMinute}
                label={t('endTime')}
                minuteToDisable={minuteToDisable}
                className={`${styles.DayTimeInput} ${styles.WorkEnd}`}
            />
            {showPause &&
                (!hasPause ? (
                    <div className={styles.PauseToggle}>
                        <ButtonIcon icon={<Add />} onClick={addPause} className={styles.PauseButton} />
                    </div>
                ) : (
                    <>
                        <TimePickerComponent
                            value={pauseStart}
                            label={t('startTime')}
                            setValue={setPauseStart}
                            minHour={endMinHour}
                            minMinute={endMinMinute}
                            maxHour={startMaxHour}
                            maxMinute={startMaxMinute}
                            endTime={pauseEnd || end}
                            minuteToDisable={minuteToDisable}
                            className={`${styles.DayTimeInput} ${styles.PauseStart}`}
                        />
                        <TimePickerComponent
                            value={pauseEnd}
                            label={t('endTime')}
                            setValue={setPauseEnd}
                            minHour={endMinHour}
                            minMinute={endMinMinute}
                            maxHour={startMaxHour}
                            maxMinute={startMaxMinute}
                            startTime={pauseStart || start}
                            minuteToDisable={minuteToDisable}
                            className={`${styles.DayTimeInput} ${styles.PauseEnd}`}
                        />
                    </>
                ))}
            {showPreparationFields && (
                <>
                    <SelectMultiple
                        selectOptions={options}
                        selectValues={preparationBefore}
                        setSelectValues={setPreparationBefore}
                        label={t('preparationTasksBefore')}
                        className={`${styles.DayTimeInput} ${styles.PreparationBefore}`}
                        actionItem={actionItem}
                        reopenTrigger={reopenTrigger}
                    />
                    <SelectMultiple
                        selectOptions={options}
                        selectValues={preparationAfter}
                        setSelectValues={setPreparationAfter}
                        label={t('preparationTasksAfter')}
                        className={`${styles.DayTimeInput} ${styles.PreparationAfter}`}
                        actionItem={actionItem}
                        reopenTrigger={reopenTrigger}
                    />
                    {manageTasksDialog}
                </>
            )}
        </div>
    )
}

export default DayTimeRow
